/** Google Maps JSON styles — Find a Bakery brand refresh (Figma light pastel map) */

export const BRAND_REFRESH_MAP_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#f5f0e8' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#6b6470' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f0e8' }] },
  // Hide Google business / place pins so only our cookie marker shows
  {
    featureType: 'poi',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi.business',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ visibility: 'on', color: '#e5f2d4' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [{ visibility: 'on', color: '#6b8f5e' }],
  },
  // Hide neighborhood labels that sit under our pin (e.g. "TRIBECA")
  {
    featureType: 'administrative.neighborhood',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'administrative.land_parcel',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'transit',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'transit',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#ffffff' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#e8e2d8' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#faf8f4' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#c8dff5' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#7a9eb8' }],
  },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#ebe6de' }],
  },
];

export const getBrandRefreshUserLocationIcon = (google) => ({
  path: google.maps.SymbolPath.CIRCLE,
  scale: 10,
  fillColor: '#BFA1FF',
  fillOpacity: 1,
  strokeColor: '#FFFFFF',
  strokeWeight: 3,
});

/**
 * Figma Map pin component = 50×50 (see node 6202:146608).
 * Default + selected share the same box so click doesn't resize.
 */
const BRAND_REFRESH_DEFAULT_MARKER = { width: 50, height: 50 };
const BRAND_REFRESH_SELECTED_MARKER = { width: 50, height: 50 };

export const getBrandRefreshStoreMarkerIcon = (
  google,
  isSelected,
  markerUrl,
  selectedMarkerUrl
) => {
  const size = isSelected
    ? BRAND_REFRESH_SELECTED_MARKER
    : BRAND_REFRESH_DEFAULT_MARKER;
  const url =
    isSelected && selectedMarkerUrl ? selectedMarkerUrl : markerUrl;

  if (!google?.maps?.Size || !google?.maps?.Point) {
    return { url };
  }

  return {
    url,
    scaledSize: new google.maps.Size(size.width, size.height),
    // Anchor at shadow / ground so selected tip floats (Figma lift)
    anchor: new google.maps.Point(size.width / 2, size.height),
  };
};

/**
 * when the browser has no location access, default the map to
 * Philadelphia and rely on fitBrandRefreshLocationsViewport to zoom out so
 * nearby stores stay visible. (Philadelphia City Hall.)
 *
 * Ticket 18773 helpers (fit viewport / Philly default) live here so Results +
 * Store Details share one map-styles module.
 */
export const BRAND_REFRESH_DEFAULT_CENTER = { lat: 39.9526, lng: -75.1652 };

/** ~1 mile — ticket: min radius around location when zooming */
export const BRAND_REFRESH_MIN_RADIUS_METERS = 1609;

/** Prevent tightly zooming onto a single point with no stores visible */
export const BRAND_REFRESH_MAX_FIT_ZOOM = 15;

const METERS_PER_DEGREE_LAT = 111320;

const toLatLngLiteral = (point) => {
  if (!point || point.lat == null || point.lng == null) {
    return null;
  }
  const lat = parseFloat(point.lat);
  const lng = parseFloat(point.lng);
  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    return null;
  }
  return { lat, lng };
};

/**
 * Expand viewport so search/user location + nearby stores are visible
 * (or at least a minimum radius around the location).
 */
export const fitBrandRefreshLocationsViewport = (
  map,
  google,
  { stores = [], center = null, minRadiusMeters = BRAND_REFRESH_MIN_RADIUS_METERS } = {}
) => {
  if (!map || !google?.maps?.LatLngBounds) {
    return false;
  }

  const bounds = new google.maps.LatLngBounds();
  let pointCount = 0;

  const centerLatLng = toLatLngLiteral(center);
  if (centerLatLng) {
    bounds.extend(centerLatLng);
    pointCount += 1;
  }

  stores.forEach((store) => {
    const storeLatLng = toLatLngLiteral(store);
    if (storeLatLng) {
      bounds.extend(storeLatLng);
      pointCount += 1;
    }
  });

  if (pointCount === 0) {
    return false;
  }

  // Guarantee a minimum visible radius around the search/user location
  if (centerLatLng && minRadiusMeters > 0) {
    const latDelta = minRadiusMeters / METERS_PER_DEGREE_LAT;
    const lngDelta =
      minRadiusMeters /
      (METERS_PER_DEGREE_LAT * Math.cos((centerLatLng.lat * Math.PI) / 180) || 1);
    bounds.extend({
      lat: centerLatLng.lat + latDelta,
      lng: centerLatLng.lng,
    });
    bounds.extend({
      lat: centerLatLng.lat - latDelta,
      lng: centerLatLng.lng,
    });
    bounds.extend({
      lat: centerLatLng.lat,
      lng: centerLatLng.lng + lngDelta,
    });
    bounds.extend({
      lat: centerLatLng.lat,
      lng: centerLatLng.lng - lngDelta,
    });
  }

  map.fitBounds(bounds, 48);

  if (google.maps.event?.addListenerOnce) {
    google.maps.event.addListenerOnce(map, 'idle', () => {
      const zoom = map.getZoom();
      if (typeof zoom === 'number' && zoom > BRAND_REFRESH_MAX_FIT_ZOOM) {
        map.setZoom(BRAND_REFRESH_MAX_FIT_ZOOM);
      }
    });
  }

  return true;
};
